/**
 * Action Required Inbox — surfaces items that need human attention,
 * grouped by category. Mounts on the Dashboard between the header and the
 * stat cards. Auto-refreshes every 60 seconds.
 *
 * Three streams:
 *   1. Rejected claims awaiting triage
 *   2. Live ODM batches transmitted >24h ago with no 999/277CA
 *   3. Timesheet entries flagged for major-drift / supervisor review
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Warning,
  WarningCircle,
  Clock,
  XCircle,
  ArrowRight,
  CaretDown,
  CaretUp,
  CheckCircle,
  PaperPlaneTilt,
  Files,
} from '@phosphor-icons/react';

const formatTimeAgo = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const StreamCard = ({ icon: Icon, color, accent, title, count, items, emptyText, renderItem, onViewAll, viewAllLabel, dataTestid }) => {
  const [expanded, setExpanded] = useState(count > 0 && count <= 3);
  const isEmpty = count === 0;

  return (
    <div
      className={`border rounded-xl overflow-hidden ${
        isEmpty ? 'border-slate-200 bg-white' : `${accent} bg-white`
      }`}
      data-testid={dataTestid}
    >
      <button
        className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors ${isEmpty ? 'opacity-60' : ''}`}
        onClick={() => !isEmpty && setExpanded(!expanded)}
        disabled={isEmpty}
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
            <Icon size={18} weight="fill" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
              {!isEmpty && (
                <Badge className={`${accent.replace('border-', 'bg-').replace('-200', '-100')} ${accent.replace('border-', 'text-').replace('-200', '-800')} text-[10px]`}>
                  {count}
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEmpty ? emptyText : `${count === 1 ? 'item needs' : `${count} items need`} action`}
            </p>
          </div>
        </div>
        {!isEmpty && (
          <div className="flex items-center gap-2">
            <CaretDown
              size={14}
              className={`text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
          </div>
        )}
      </button>

      {expanded && !isEmpty && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {items.slice(0, 5).map(renderItem)}
          {count > 5 && onViewAll && (
            <div className="px-4 py-2 bg-slate-50">
              <Button
                size="sm"
                variant="ghost"
                onClick={onViewAll}
                className="w-full justify-center text-xs"
                data-testid={`${dataTestid}-view-all`}
              >
                View all {count} <ArrowRight size={12} className="ml-1.5" />
              </Button>
            </div>
          )}
          {count <= 5 && onViewAll && (
            <div className="px-4 py-2 bg-slate-50">
              <Button
                size="sm"
                variant="ghost"
                onClick={onViewAll}
                className="w-full justify-center text-xs"
                data-testid={`${dataTestid}-view-all`}
              >
                {viewAllLabel || 'Open in dedicated view'} <ArrowRight size={12} className="ml-1.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ActionRequiredInbox = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/action-items');
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const id = setInterval(fetchData, 60000);
    return () => clearInterval(id);
  }, [fetchData]);

  if (loading || !data) return null;

  const isAllClear = data.total === 0;

  return (
    <div className="border border-slate-200 rounded-2xl bg-gradient-to-br from-white to-slate-50/50 overflow-hidden" data-testid="action-required-inbox">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isAllClear ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {isAllClear ? <CheckCircle size={20} weight="fill" /> : <Warning size={20} weight="fill" />}
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              {isAllClear ? 'All clear' : `${data.total} action${data.total === 1 ? '' : 's'} required`}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isAllClear
                ? 'No claims, batches, or EVV entries need attention right now'
                : 'Triage these to keep your billing pipeline moving'}
            </p>
          </div>
        </div>
        {!isAllClear && (
          <CaretUp
            size={16}
            className={`text-slate-400 transition-transform ${collapsed ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* Streams */}
      {!collapsed && !isAllClear && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 pt-0">
          {/* Rejected claims */}
          <StreamCard
            dataTestid="inbox-rejected-claims"
            icon={XCircle}
            color="bg-red-100 text-red-700"
            accent="border-red-200"
            title="Rejected claims"
            count={data.rejected_claims.count}
            items={data.rejected_claims.items}
            emptyText="No rejected claims"
            onViewAll={() => navigate('/claims?status=Rejected')}
            viewAllLabel="Open Rejected filter"
            renderItem={(c) => (
              <button
                key={c.id}
                className="w-full px-4 py-2 hover:bg-red-50/50 text-left flex items-center justify-between gap-3"
                onClick={() => navigate(`/claims?status=Rejected&claim=${c.id}`)}
                data-testid={`inbox-rejected-${c.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-mono text-slate-900 truncate">{c.claim_number}</p>
                  <p className="text-[11px] text-slate-500 truncate mt-0.5">
                    {c.patient_name} · {formatDate(c.service_date)}
                    {c.rejection_reason ? ` · ${c.rejection_reason}` : ''}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-semibold text-slate-900">
                    {formatCurrency(c.estimated_reimbursement)}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{formatTimeAgo(c.updated_at)}</p>
                </div>
              </button>
            )}
          />

          {/* Stale ODM batches */}
          <StreamCard
            dataTestid="inbox-stale-batches"
            icon={Clock}
            color="bg-amber-100 text-amber-700"
            accent="border-amber-200"
            title="Stale ODM batches"
            count={data.stale_submissions.count}
            items={data.stale_submissions.items}
            emptyText="No batches awaiting response"
            onViewAll={() => navigate('/claims')}
            viewAllLabel="Check Recent Submissions"
            renderItem={(s) => (
              <div
                key={s.batch_id}
                className="w-full px-4 py-2 hover:bg-amber-50/50 flex items-center justify-between gap-3"
                data-testid={`inbox-stale-${s.batch_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-mono text-slate-900 truncate" title={s.filename}>
                    {s.filename || s.batch_id}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {s.claim_count} claim{s.claim_count === 1 ? '' : 's'} · waiting {s.hours_waiting}h
                  </p>
                </div>
                <Badge className="bg-amber-100 text-amber-800 text-[10px] shrink-0">No 999/277</Badge>
              </div>
            )}
          />

          {/* EVV drift */}
          <StreamCard
            dataTestid="inbox-evv-drift"
            icon={WarningCircle}
            color="bg-purple-100 text-purple-700"
            accent="border-purple-200"
            title="EVV drift / supervisor review"
            count={data.evv_drift.count}
            items={data.evv_drift.items}
            emptyText="No EVV drift to review"
            onViewAll={() => navigate('/timesheets')}
            viewAllLabel="Review in Timesheets"
            renderItem={(e) => (
              <button
                key={`${e.timesheet_id}-${e.patient_name}-${e.service_date}`}
                className="w-full px-4 py-2 hover:bg-purple-50/50 text-left flex items-center justify-between gap-3"
                onClick={() => navigate(`/timesheets?ts=${e.timesheet_id}`)}
                data-testid={`inbox-drift-${e.timesheet_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-900 truncate">
                    {e.patient_name || 'Unknown patient'}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate mt-0.5">
                    {formatDate(e.service_date)} · {e.recommendation === 'major_drift' ? 'Major drift' : 'Supervisor review'}
                    {e.drift_minutes ? ` · ${e.drift_minutes}m` : ''}
                  </p>
                </div>
                <Badge
                  className={`text-[10px] shrink-0 ${
                    e.recommendation === 'supervisor_required'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}
                >
                  {e.recommendation === 'supervisor_required' ? 'Sup. req.' : 'Drift'}
                </Badge>
              </button>
            )}
          />
        </div>
      )}

      {/* All clear hides streams entirely */}
      {isAllClear && !collapsed && (
        <div className="px-5 pb-5 grid grid-cols-3 gap-3">
          {[
            { icon: XCircle, label: 'Rejected claims', color: 'text-red-600' },
            { icon: PaperPlaneTilt, label: 'ODM batches', color: 'text-amber-600' },
            { icon: Files, label: 'EVV drift', color: 'text-purple-600' },
          ].map((s) => (
            <div
              key={s.label}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg"
            >
              <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />
              <s.icon size={14} className={`${s.color} shrink-0`} />
              <span className="text-xs text-slate-600 truncate">0 {s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
